import React from 'react';
import { useParams } from 'react-router-dom';

import { Anchor, Button, Loader, Paper, Stack, Text, Title } from '@cowbell/ui';

import {
  getProofOfLoss,
  uploadProofOfLossDocument,
} from '../../api/claims/proof-of-loss.api';
import type { ProofOfLossView } from '../../api/claims/responses/ProofOfLossResponses';
import PublicComponentWrapper from '../../components/wrappers/PublicComponentWrapper';
import { rem } from '../../components/panels/panelLayout';
import { manageAPIError } from '../../utils';
import { useFlowSessionTimer, withFlowRetry } from '../flow/flowSession';
import { ProofOfLossItemRow } from './ProofOfLossItemRow';

/** The server checks all of this again and is the authority; this only spares a slow upload. */
export const MAX_UPLOAD_BYTES = 26_214_400;
export const ALLOWED_EXTENSIONS: readonly string[] = [
  'pdf',
  'png',
  'jpg',
  'jpeg',
  'heic',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'csv',
  'txt',
  'eml',
  'msg',
];

const TOO_LARGE = 'That file is larger than 25 MB.';
const WRONG_TYPE = `Allowed types: ${ALLOWED_EXTENSIONS.join(', ')}.`;
const UPLOAD_FALLBACK = 'The upload did not go through. Please try again.';
const ENDED =
  'Your session has ended. Open the link in your email again to get a new code.';
const INVALID =
  'This link is no longer valid. Ask your adjuster to send a new one.';
const DONE =
  'Nothing else is needed right now. Thank you. Your adjuster will review what you sent.';

type Screen =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly view: ProofOfLossView }
  | { readonly kind: 'ended' }
  | { readonly kind: 'invalid' }
  | { readonly kind: 'failed'; readonly message: string };

const statusOf = (error: unknown): number | null =>
  (error as { statusCode?: number | null })?.statusCode ?? null;

const documentsHeading = (companyName?: string | null): string =>
  companyName?.trim()
    ? `Documents for ${companyName.trim()}`
    : 'Documents for your claim';

const refuseFile = (file: File): string | null => {
  if (file.size > MAX_UPLOAD_BYTES) {
    return TOO_LARGE;
  }
  const extension = file.name.includes('.')
    ? file.name.split('.').pop()?.toLowerCase()
    : undefined;
  if (!extension || !ALLOWED_EXTENSIONS.includes(extension)) {
    return WRONG_TYPE;
  }
  return null;
};

interface Uploading {
  readonly key: string;
  readonly percent: number;
}

export function ProofOfLossPage() {
  const { claimId, lossItemId } = useParams<{
    claimId: string;
    lossItemId: string;
  }>();
  const { workflowId } = useFlowSessionTimer();
  const [screen, setScreen] = React.useState<Screen>({ kind: 'loading' });
  const [uploading, setUploading] = React.useState<Uploading | null>(null);
  const [rowErrors, setRowErrors] = React.useState<Record<string, string>>({});
  const [uploaded, setUploaded] = React.useState<readonly string[]>([]);

  /** Sessions that ended and links that are dead end the page; anything else is the caller's to show. */
  const endIfRefused = (error: unknown): boolean => {
    const status = statusOf(error);
    if (status === 401) {
      setScreen({ kind: 'ended' });
      return true;
    }
    if (status === 403) {
      setScreen({ kind: 'invalid' });
      return true;
    }
    return false;
  };

  const load = React.useCallback(async () => {
    setScreen({ kind: 'loading' });
    try {
      const { data } = await withFlowRetry(() =>
        getProofOfLoss(claimId, lossItemId)
      );
      setScreen({ kind: 'ready', view: data });
    } catch (error) {
      if (endIfRefused(error)) {
        return;
      }
      setScreen({
        kind: 'failed',
        message: manageAPIError(
          error as never,
          'The list of documents could not be loaded.'
        ) as string,
      });
    }
    // endIfRefused only calls state setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId, lossItemId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const upload = async (key: string, file: File) => {
    const refusal = refuseFile(file);
    setRowErrors({ ...(refusal ? { [key]: refusal } : {}) });
    if (refusal) {
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    setUploading({ key, percent: 0 });
    try {
      const { data } = await withFlowRetry(() =>
        uploadProofOfLossDocument(claimId, lossItemId, key, {
          data: formData,
          onUploadProgress: (event) =>
            setUploading({
              key,
              percent: Math.round(
                ((event.loaded ?? 0) / (event.total || file.size || 1)) * 100
              ),
            }),
        })
      );
      setScreen({ kind: 'ready', view: data });
      setUploaded((names) => [...names, file.name]);
    } catch (error) {
      if (!endIfRefused(error)) {
        setRowErrors({
          [key]: manageAPIError(error as never, UPLOAD_FALLBACK) as string,
        });
      }
    } finally {
      setUploading(null);
    }
  };

  return (
    <PublicComponentWrapper>
      <Paper p="lg" radius="md" maw={rem(720)} mx="auto" withBorder>
        <Body
          screen={screen}
          workflowId={workflowId}
          uploading={uploading}
          rowErrors={rowErrors}
          uploaded={uploaded}
          onFile={upload}
          onRetry={load}
        />
      </Paper>
    </PublicComponentWrapper>
  );
}

interface BodyProps {
  readonly screen: Screen;
  readonly workflowId: string | null;
  readonly uploading: Uploading | null;
  readonly rowErrors: Readonly<Record<string, string>>;
  readonly uploaded: readonly string[];
  readonly onFile: (key: string, file: File) => void;
  readonly onRetry: () => void;
}

function Body({
  screen,
  workflowId,
  uploading,
  rowErrors,
  uploaded,
  onFile,
  onRetry,
}: BodyProps) {
  switch (screen.kind) {
    case 'loading':
      return <Loader />;
    case 'ended':
      return (
        <Stack gap="sm">
          <Text>{ENDED}</Text>
          {workflowId && (
            <Anchor href={`/validate/${encodeURIComponent(workflowId)}`}>
              Get a new code
            </Anchor>
          )}
        </Stack>
      );
    case 'invalid':
      return <Text>{INVALID}</Text>;
    case 'failed':
      return (
        <Stack gap="sm" align="flex-start">
          <Text>{screen.message}</Text>
          <Button onClick={onRetry}>Retry</Button>
        </Stack>
      );
    default:
      return (
        <Stack gap="lg">
          <Stack gap={rem(2)}>
            <Title order={2}>{documentsHeading(screen.view.companyName)}</Title>
            <Text c="dimmed">{`Claim ${screen.view.claimNumber}`}</Text>
          </Stack>
          <Text size="sm">We will never ask for bank details here.</Text>
          {uploaded.map((name, index) => (
            // The same file name can be sent twice, so the position is part of the key.
            // eslint-disable-next-line react/no-array-index-key
            <Text key={`${name}-${index}`} size="sm" c="green.8">
              {`Uploaded ${name}`}
            </Text>
          ))}
          {screen.view.items.length === 0 && <Text>{DONE}</Text>}
          {screen.view.items.map((item) => (
            <ProofOfLossItemRow
              key={item.key}
              item={item}
              disabled={uploading !== null}
              progress={uploading?.key === item.key ? uploading.percent : null}
              error={rowErrors[item.key]}
              onFile={(file) => onFile(item.key, file)}
            />
          ))}
        </Stack>
      );
  }
}
